import {
  useIsAuthenticated,
} from "@azure/msal-react";
import {
  styled,
} from "@mui/material";
import { axiosSecuredInstance } from "configurations/axiosConfig";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  gaps,
  padding,
  zIndex,
} from "theme/defaultStyle";
import { PERMISSIONS, SiteErrorType } from "utils/data/enum";
import {
  startNotifying,
  stopNotifying,
} from "utils/services/ContextProgressNotifierService";
import theme from "../theme/default";
import { LayoutBodyContext } from "./LayoutBodyContext";
import Footer from "./components/Footer";
import Header from "./components/Header";
import { SiteErrorMessage } from "./components/SiteErrorMessage";
import GlobalErrorBoundary from "./components/GlobalErrorBoundary";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import useNavRedirect from "utils/custom-hooks/useNavRedirect";
import { menuDefinitions, menuType } from "pages/home/menu";
import useStewartTableStore from "utils/context/StewartTableContext";
import ToastDatePicker from "controls/global/datepicker-search/ToastDatePicker";
import useGlobal from "utils/custom-hooks/useGlobal";

type Props = {
  children?: any; // ReactElement<string> | undefined; // any; //ReactNode;
  permissions?: PERMISSIONS[]; // string[];
};

const RootContainer = styled("div")((props) => ({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  // gap: gaps.large1,
  justifyContent: "space-between",
  padding: `0 ${padding.xlarge21}`,
  position: "sticky",
  width: "100%",
  zIndex: zIndex.high0,
  minHeight :"100vh",
  "&.show-global-access": {
    gap: gaps.zero,
  },
  [theme.breakpoints.down("md")]: {
    padding: `0 ${padding.xsmall1}`,
  },
  "& > :first-child": {
    flexGrow: 0,
    flexShrink: 0,
    position: "sticky",
    top: 0,
  },
}));

const BodyContainer = styled("div")< { isHomepage: boolean }>(({ isHomepage }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: isHomepage ? "center" : "flex-start",
  alignItems: isHomepage ? "center" : "stretch",
  flex: 1,
  gap: gaps.zero,
}));

enum LoginStatus {
  LoggingIn,
  Valid,
  Invalid,
  TimedOut,
  OtherError
}

const PrivatePage = ({ children, permissions }: Props) => {
  const [isValidLogin, setIsValidLogin] = useState(false);
  const [isDisabled, ] = useState(false);
  const [loginStatus, setLoginStatus] = useState<LoginStatus>();
  const [pageContent, setPageContent] = useState<JSX.Element | undefined>();
  const [loginProgressPercent, setLoginProgressPercent] = useState<number | undefined>();
  const bodyRef = useRef<HTMLDivElement>(null);
  const saveFileRef = useRef<HTMLButtonElement>();
  const saveFileChangeRef = useRef<HTMLButtonElement>();
  const tabSaveChangesRef = useRef<HTMLButtonElement>();
  const isAuthenticated = useIsAuthenticated();

  const processLogin = useMemo(
    () => async (callback: (status: LoginStatus) => void) => {
      startNotifying("login", setLoginProgressPercent);
      //Call DB from here to ensure that the user is valid.
      let status = LoginStatus.LoggingIn;
      callback(status);

      try {
        const response = await axiosSecuredInstance.get("/user/processLogin",
          {
            timeout: 15000,
            timeoutErrorMessage: "TIMEOUT",
          });

        const data = response.data;
        if (data && data.LoginResult && data.LoginResult === 1) {
          status = LoginStatus.Valid;
        }
        else {
          status = LoginStatus.Invalid;
        }
      } catch (error: any) {
        if (String(error?.message).includes("TIMEOUT")) {
          status = LoginStatus.TimedOut;
        }
        else {
          status = LoginStatus.OtherError;
        }
      }
      finally {
        stopNotifying("login", setLoginProgressPercent);
      }

      callback(status);
    }, []);

  const getErrorType = (): SiteErrorType | undefined => {
    let errorType: SiteErrorType | undefined = undefined;

    if (loginStatus === LoginStatus.Invalid || profileSettings.isUserProfileSet === false) {
      errorType = SiteErrorType.BadAccount;
    }
    else if (loginStatus === LoginStatus.OtherError || loginStatus === LoginStatus.TimedOut) {
      errorType = SiteErrorType.NetworkError;
    }

    return errorType;
  };

  const StyledToast = styled(ToastDatePicker)({
    margin: "auto",
    zIndex: 1000,
  });

  useEffect(() => {
    const tryLogin = async () => {
      if (isAuthenticated)
        processLogin((status: LoginStatus) => {
          setLoginStatus(status);
        });
      else {
        setLoginStatus(LoginStatus.Invalid);
      }
    };

    tryLogin();
  }, [isAuthenticated, processLogin]);

  const [{ profileSettings }] = useProfileSettingsCache();
  const permissionCodes = (profileSettings?.userPermissionItemTypes as string[]);
  const { redirect } = useNavRedirect();
  const { checkIfPermissionIsAssigned } = useGlobal();
  const [{showToast}, { setShowToast }] = useStewartTableStore();
  const haspermissionToAccessModule = () => {
    const permissionsEnumArray = Object.entries(PERMISSIONS).map(([key, value]) => (value))
    return permissionsEnumArray.some(item => permissionCodes.includes(item));
  }

  const hasAccessToAnyMenuItems = () => {
    const mainMenus = menuDefinitions?.filter((menu) => menu.type === menuType.mainMenu);
    const mainMenusPermissions = mainMenus.map(menu => menu?.permissionCode);
    return mainMenusPermissions.some(menuPermissions => checkIfPermissionIsAssigned(menuPermissions));
  }

  useEffect(() => {
    let controlToShow = undefined;
    if (loginStatus === LoginStatus.Valid && profileSettings.isUserProfileSet) {
      controlToShow = React.cloneElement(children, {
        saveFileRef,
        saveFileChangeRef,
        tabSaveChangesRef,
      });
    }
    setPageContent(controlToShow);
    setIsValidLogin(loginStatus === LoginStatus.Valid);
    // isEditMode ? setIsDisabled(true) : setIsDisabled(false);

    // check if user has permission to access any module
    if (permissionCodes?.length > 0 && !haspermissionToAccessModule()) {
      redirect("/nomoduleaccess", null);
    }
    // check if user has permission to access routed module
    if (permissions && permissionCodes?.length > 0 && !checkIfPermissionIsAssigned(permissions)) {
      redirect("/404", null);
    }
    //check if user has permission to any one of the menu items
    if(permissionCodes?.length > 0 && !hasAccessToAnyMenuItems()){
      redirect("/bad-account-message", null);
    }
  }, [loginStatus, profileSettings.isUserProfileSet, children ]);
  
  const handleCloseToast =()=>{
    setShowToast(false)
  }

  return (
    <RootContainer
      id="root-container"
      className=""
    >
      <Header
        {...{ isValidLogin, saveFileRef, saveFileChangeRef, isDisabled }}
      />
      <GlobalErrorBoundary>
        <LayoutBodyContext.Provider value={bodyRef}>
          <BodyContainer ref={bodyRef} id="body-container" isHomepage={pageContent?.type.name === "HomePage"}>
            {pageContent ? (
              <div>{pageContent}</div>
            ) : (
              <SiteErrorMessage
                errorType={getErrorType()}
                progressPercent={loginProgressPercent}
              />
            )}
          </BodyContainer>
          {showToast && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  position: "fixed",
                  top: "calc(100vh - 160px)",
                  right: "10px",
                }}
              >
                <StyledToast
                  title="Oops"
                  message="The date selected is outside the range specified in the advanced search."
                  onClose={handleCloseToast}
                />
              </div>
            )}
          <Footer />
        </LayoutBodyContext.Provider>
      </GlobalErrorBoundary>
    </RootContainer>
  );
};

export default PrivatePage;
